import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import WorkCard from "@/components/feature/WorkCard";
import { categoryBySlug, categories } from "@/mocks/categories";
import { worksByCategory, works } from "@/mocks/works";
import { siteInfo } from "@/mocks/site";
import NotFound from "@/pages/NotFound";

const sortOptions = [
  { key: "update", label: "最新更新" },
  { key: "hot", label: "最热" },
  { key: "rating", label: "评分最高" },
] as const;

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const cat = categoryBySlug(slug);
  const [sort, setSort] = useState<(typeof sortOptions)[number]["key"]>("update");

  useSeo({
    title: cat
      ? `汗汗漫画${cat.name}漫画在线看_${cat.name}漫画追更专区`
      : "分类未找到 - 汗汗漫画",
    description: cat
      ? `汗汗漫画${cat.name}漫画追更专区，汇集${cat.name}类烧脑反转佳作，${cat.desc}在线看追更入口，满足你的推理阅读需求。`
      : siteInfo.description,
    keywords: cat
      ? `汗汗漫画,汗汗漫画${cat.name},${cat.name}漫画,${cat.name}漫画在线看`
      : undefined,
    jsonLd: cat
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `汗汗漫画${cat.name}漫画专区`,
            url: `${siteInfo.url}/category/${cat.slug}`,
            description: cat.desc,
            mainEntity: {
              "@type": "ItemList",
              itemListElement: worksByCategory(cat.slug).map((w, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: w.title,
                url: `${siteInfo.url}/work/${w.id}`,
              })),
            },
          },
        ]
      : undefined,
  });

  if (!cat) {
    return <NotFound />;
  }

  const list = worksByCategory(cat.slug);
  const sorted = [...list].sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "hot") {
      const num = (s: string) => parseFloat(s);
      return num(b.views) - num(a.views);
    }
    return 0;
  });
  const otherCats = categories.filter((c) => c.slug !== cat.slug);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
      {/* 面包屑 */}
      <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
        <Link to="/" className="cursor-pointer transition-colors hover:text-accent-300">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-200">分类</span>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="font-medium text-accent-300">{cat.name}漫画</span>
      </nav>

      {/* 分类头部 */}
      <div className="relative mt-6 overflow-hidden rounded-lg border border-background-200/60 bg-background-100 p-6 md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
              <i className={`${cat.icon} text-2xl`} aria-hidden="true"></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
                {cat.name}漫画
                <span className="ml-3 align-middle text-sm font-normal text-foreground-500">
                  {cat.en} · {cat.tagline}
                </span>
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{cat.desc}</p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-accent-300/40 bg-accent-500/10 px-4 py-1.5 text-xs font-medium text-accent-200">
            <i className="ri-file-list-3-line" aria-hidden="true"></i>
            共 {list.length} 部作品
          </span>
        </div>
      </div>

      {/* 相关分类 */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-500">同类推荐：</span>
        {otherCats.map((c) => (
          <Link
            key={c.slug}
            to={`/category/${c.slug}`}
            className="cursor-pointer rounded-full border border-background-200/60 bg-background-100 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:border-accent-300/50 hover:text-accent-200"
          >
            {c.name}漫画
          </Link>
        ))}
      </div>

      {/* 排序 */}
      <div className="mt-8 flex items-center gap-2 border-b border-background-200/60 pb-4">
        <span className="text-sm text-foreground-500">排序：</span>
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {sortOptions.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setSort(o.key)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                sort === o.key
                  ? "bg-primary-500 font-medium text-background-50"
                  : "text-foreground-400 hover:text-foreground-200"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* 作品网格 */}
      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {sorted.map((w) => (
          <WorkCard key={w.id} work={w} />
        ))}
      </div>

      {/* 底部相关作品（全站推荐） */}
      <div className="mt-14 border-t border-background-200/60 pt-10">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">
          <i className="ri-fire-line text-primary-400" aria-hidden="true"></i>
          更多烧脑佳作
        </h2>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {works.slice(0, 4).map((w) => (
            <WorkCard key={w.id} work={w} />
          ))}
        </div>
      </div>
    </div>
  );
}